import { useEffect, useMemo, useRef, useState } from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import TimeSeriesGraph from "../timeSeriesGraph/index";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import {
  FileInput,
  Select,
  Button,
  Grid,
  useMantineColorScheme,
} from "@mantine/core";
import {
  IconCalendar,
  IconCaretDownFilled,
  IconFileSearch,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { generateTimeSeries } from "../../utils/helpers";
import "../loadAttributesForm/index.scss";
import { responsiveCol, useAssessmentRouteParams } from "../../constants/helper";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";

const EVCSAttributesForm = ({
  form,
  evcsParameters,
  dataAssetEvcs,
  startDate,
  loadStartDate,
  updateEvcsState,
  handleFileInputChange,
  handleFileClick,
  handleFileDownload,
}) => {
  const { t } = useTranslation();
  const { designVersionId } = useAssessmentRouteParams();
  const [helpMenuData] = useAtom(helpContextMenuAtom);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const { colorScheme } = useMantineColorScheme();

  const containerRef = useRef(null);
  const timeInputRef = useRef();
  const graphRef = useRef();

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  const profileDetail = evcsParameters?.profileDetail || {};
  const intervalTime = profileDetail.timeResolution;
  const timeSeriesUnit = profileDetail.units;

  const memoDataAsset = useMemo(() => dataAssetEvcs, [dataAssetEvcs]);
  const memoChartStartDate = useMemo(() => startDate, [startDate]);
  const memoLoadStartDate = useMemo(() => loadStartDate, [loadStartDate]);
  const memoInterval = useMemo(() => intervalTime, [intervalTime]);
  const memoYAxisName = useMemo(() => timeSeriesUnit, [timeSeriesUnit]);

  const currentDate = new Date();
  const reducedDate = new Date(currentDate);
  reducedDate.setDate(currentDate.getDate() - 1);
  reducedDate.setFullYear(currentDate.getFullYear() - 1);

  const grayIconStyle = useMemo(() => ({ padding: "4px" }), [colorScheme]);

  // Delayed chart mount after data arrives
  const [chartDelayReady, setChartDelayReady] = useState(false);
  const delayTimerRef = useRef(null);
  useEffect(() => {
    const hasData = dataAssetEvcs?.length > 0;
    if (!hasData) {
      setChartDelayReady(false);
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      return;
    }
    if (chartDelayReady) return;
    if (delayTimerRef.current) return;
    delayTimerRef.current = setTimeout(() => {
      setChartDelayReady(true);
      delayTimerRef.current = null;
    }, 500);
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [dataAssetEvcs, chartDelayReady]);

  useEffect(() => {
    if (dataAssetEvcs?.length > 0) {
      graphRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [dataAssetEvcs, designVersionId]);

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.load?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
    };
  };

  const isDaily = profileDetail.recurrence === "DAILY";

  return (
    <div ref={containerRef}>
      <Grid gutter="md" className="mt-2 mb-2">
        {!isDaily && <Grid.Col {...responsiveCol}>
          <DateInput
            {...getFieldAttributes("field-5")}
            data-id="field-5"
            dropdownType="modal"
            label={t("startDate")}
            clearable
            {...form.getInputProps(
              "componentAttributes.evcsParameters.profileDetail.startDate"
            )}
            onChange={(date) => {
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "startDate",
                date
              );
              updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
              updateEvcsState("componentAttributes.evcsParameters.profileDetail", "evcsFileClicked", false);
              form.setFieldValue("componentAttributes.evcsParameters.profileDetail.evcsFile", null);
            }}
            placeholder={t("startDate")}
            valueFormat="DD-MMM-YYYY"
            required
            rightSection={
              <div style={grayIconStyle}>
                <IconCalendar size="1.1rem" stroke={1.5} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            popoverProps={{ withinPortal: true, position: "bottom-start" }}
            maxDate={reducedDate}
          />
        </Grid.Col>}
        {!isDaily && <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-6")}
            data-id="field-6"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("startTime")}
            data={generateTimeSeries()}
            value={profileDetail.startTime}
            {...form.getInputProps(
              "componentAttributes.evcsParameters.profileDetail.startTime"
            )}
            onChange={(value) => {
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "startTime",
                value
              );
              updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
              updateEvcsState("componentAttributes.evcsParameters.profileDetail", "evcsFileClicked", false);
              form.setFieldValue("componentAttributes.evcsParameters.profileDetail.evcsFile", null);
            }}
            required
            ref={timeInputRef}
            size="sm"
            withinPortal={true}
            styles={{ dropdown: { left: 0, width: "100px", maxWidth: "100px" } }}
            searchable
          />
        </Grid.Col>}
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-7")}
            data-id="field-7"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("timeResolution")}
            data={[
              { value: 15, label: "15" },
              { value: 30, label: "30" },
              { value: 60, label: "60" },
            ]}
            value={profileDetail.timeResolution}
            {...form.getInputProps(
              "componentAttributes.evcsParameters.profileDetail.timeResolution"
            )}
            onChange={(value) => {
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "timeResolution",
                value
              );
              updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
              updateEvcsState("componentAttributes.evcsParameters.profileDetail", "evcsFileClicked", false);
              form.setFieldValue("componentAttributes.evcsParameters.profileDetail.evcsFile", null);
            }}
            required
            withinPortal
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-8")}
            data-id="field-8"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("pv.units")}
            data={[
              { value: "kW", label: "kW" },
              { value: "W", label: "W" },
              { value: "kWh", label: "kWh", disabled: true },
              { value: "Wh", label: "Wh", disabled: true },
            ]}
            value={profileDetail.units}
            required
            {...form.getInputProps(
              "componentAttributes.evcsParameters.profileDetail.units"
            )}
            onChange={(value) => {
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "units",
                value
              );
              updateEvcsState("componentAttributes.evcsParameters", "profileFile", []);
              updateEvcsState("componentAttributes.evcsParameters.profileDetail", "evcsFileClicked", false);
              form.setFieldValue("componentAttributes.evcsParameters.profileDetail.evcsFile", null);
            }}
            withinPortal
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol} className="get-profile">
          <FileInput
            {...getFieldAttributes("field-9")}
            data-id="field-9"
            label={
              <>
                <div>
                  {t("evcs.uploadFileLabel")}
                  <span style={{ color: "#fa5252" }}> *</span>
                </div>
                <span
                  style={{
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    color: "#42b4e6",
                    cursor: "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleFileDownload("evcs");
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileDownload("evcs");
                  }}
                >
                  {t("downloadTemplate")}
                </span>
              </>
            }
            accept=".xls, .xlsx, .csv"
            {...form.getInputProps(
              "componentAttributes.evcsParameters.profileDetail.evcsFile"
            )}
            onChange={(file) => {
              updateEvcsState(
                "componentAttributes.evcsParameters",
                "profileFile",
                []
              );
              handleFileInputChange(file);
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "evcsFileClicked",
                !!file
              );
              form.setFieldValue(
                "componentAttributes.evcsParameters.profileDetail.evcsFile",
                file
              );
            }}
            rightSection={
              <div style={grayIconStyle}>
                <IconFileSearch size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col
          {...responsiveCol}
          className={
            form.errors[
              "componentAttributes.evcsParameters.profileDetail.evcsFile"
            ]
              ? "get-profile-error"
              : "get-profile-button"
          }
        >
          <Button
            variant="filled"
            onClick={() => {
              handleFileClick();
              updateEvcsState(
                "componentAttributes.evcsParameters.profileDetail",
                "evcsFileClicked",
                false
              );
            }}
            sx={{
              backgroundColor: "#626469",
              "&:hover": { backgroundColor: "#494b50" },
            }}
            disabled={!profileDetail.evcsFileClicked}
          >
            {t("getProfile")}
          </Button>
        </Grid.Col>
      </Grid>

      <Grid gutter="md" className="mb-2">
        <Grid.Col
          gutter="md"
          className="mt-2 mb-2"
          id="evcs-timeSeries"
          style={{
            position: "relative",
            maxWidth: "100% !important",
            boxSizing: "border-box",
          }}
        >
          <div
            id="evcs-scroll-into-graph"
            ref={graphRef}
            data-position="top"
          >
            {dataAssetEvcs?.length > 0 &&
              (chartDelayReady ? (
                <TimeSeriesGraph
                  dataAssset={memoDataAsset}
                  dataAssset2={[]}
                  color={"#36a2eb"}
                  name={"EVCS"}
                  chartStartDate={isDaily ? memoLoadStartDate : memoChartStartDate}
                  interval={memoInterval}
                  yAxisName={memoYAxisName}
                  embedHeight={500}
                  showMinimap={true}
                  minimapGap={40}
                  lazyLoad={true}
                  startTime={profileDetail.startTime}
                  dailyMode={isDaily}
                />
              ) : (
                <div
                  style={{
                    padding: "32px 0",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <se-loading loading={true}></se-loading>
                </div>
              ))}
          </div>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default EVCSAttributesForm;
